import { useLocation } from "react-router-dom";
import React, { FC, useState } from "react";
import * as _ from "lodash";

import {
  CtraLayout,
  Typography,
  Collapse,
  List,
  Row,
  Col,
  Button,
  Divider,
  PlusOutlined,
  Analytics
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Enterprise, MetricEntity } from "@ctra/api";

import { useDataDictionary } from "@base";
import { CompiledRoutes } from "@routes";

import { MetricPicker } from "../MetricPicker";
import { ListItem } from "../ListItem";

import styles from "./MetricSelectorPage.module.less";

const { WidgetWrapper, ContentWrapper } = CtraLayout;
const {
  V3: { CategoryIcon }
} = Analytics;
const { Paragraph } = Typography;

/**
 * Metric selector page
 * @return {JSX.Element}
 * @constructor
 */
export const MetricSelectorPage: FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();

  const [currentMetric, setCurrentMetric] = useState<MetricEntity["id"]>();

  const {
    groups: { byFamilyCategory: groupedMetrics },
    meta: { isLoading: dataDictionaryLoading, enums }
  } = useDataDictionary();

  const {
    analytics: {
      config: {
        search: { pageDescription, cta, title }
      }
    },
    layouts: {
      section: { title: layoutTitle }
    }
  } = Content;

  const handleClick = (metricID: string) =>
    Enterprise.history.push({
      pathname: CompiledRoutes.app.analytics.configure.metric({ metricID }),
      state
    });

  /**
   * Get BE enums for category which contains the order in which they should be mapped
   */
  const categoryOrder = enums["MetricCategory"];

  const sortedGroupedMetrics = _.keys(groupedMetrics).sort((a, b) => {
    const indexA = categoryOrder?.indexOf(a);
    const indexB = categoryOrder?.indexOf(b);
    return indexA - indexB;
  });

  return (
    <WidgetWrapper
      color="neutral"
      loading={dataDictionaryLoading}
      title={<Paragraph>{t<string>(pageDescription)}</Paragraph>}
    >
      <ContentWrapper className={styles.ChartPicker} padded={false}>
        <Paragraph>{t(title)}</Paragraph>
        <Divider />
        <Row gutter={[16, 0]} className={styles.MetricPicker}>
          <Col flex={1}>
            <MetricPicker value={currentMetric} onChange={setCurrentMetric} />
          </Col>
          <Col>
            <Button
              size="large"
              disabled={!currentMetric}
              type="primary"
              onClick={() => currentMetric && handleClick(currentMetric)}
              icon={<PlusOutlined />}
            >
              {t(cta)}
            </Button>
          </Col>
        </Row>
      </ContentWrapper>
      <Collapse className={styles.Collapse} expandIconPosition="right">
        {_.map(sortedGroupedMetrics, (category, idx) => {
          const perCategory = _.get(groupedMetrics, category);

          /**
           * Set the structure of the data with id and name, use family name if belongs to a metric family
           */
          const dataSource = _.map(_.keys(perCategory), (key) => {
            const lookup = [key, 0];
            const familyName = _.get(perCategory, [...lookup, "family"]);
            return {
              id: _.defaultTo(familyName, _.get(perCategory, [...lookup, "id"])),
              name: _.defaultTo(familyName, _.get(perCategory, [...lookup, "name"]))
            };
          });

          return (
            <Collapse.Panel
              header={
                <Row className={styles.Header} gutter={[24, 0]} align="middle">
                  <CategoryIcon category={category} />
                  <Col>{t(layoutTitle(category), { makeDefaultValue: true })}</Col>
                </Row>
              }
              key={idx}
            >
              <List
                dataSource={dataSource}
                renderItem={(item) => <ListItem item={item} onClick={() => handleClick(item.id)} />}
              />
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </WidgetWrapper>
  );
};
